/**
 * Sig parsing for ScriptSure prescribing — derive a per-dose × per-day estimate (and a days-supply
 * estimate) from a free-text sig line, and read the structured sigs ScriptSure stamps on a search
 * result. Used by the prescribe form to pre-fill days supply and dispense unit.
 *
 * Ported from the Medplum provider example (`medplum-provider/src/pages/meds/OrderMedicationPage.tsx`).
 */
import type { Medication } from '@medplum/fhirtypes';
import { resolveQuantityQualifier, type QualifierMatcher } from './quantityQualifiers';

/** Extension URL ScriptSure stamps a structured sig under on a `$drug-search` formulation result. */
export const SCRIPTSURE_SIG_EXTENSION = 'https://scriptsure.com/sig';

/**
 * English numbers ScriptSure routinely spells out in canonical sigs ("every eight hours"). Converted
 * to digits before the interval regexes so days-supply inference works regardless of form.
 */
const SIG_NUMBER_WORDS: Readonly<Record<string, number>> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  twenty: 20,
  twentyfour: 24,
  'twenty-four': 24,
  'twenty four': 24,
  thirty: 30,
  forty: 40,
  fortyeight: 48,
  'forty-eight': 48,
  'forty eight': 48,
  seventytwo: 72,
  'seventy-two': 72,
  'seventy two': 72,
};

const SIG_NUMBER_WORDS_ALTERNATION = Object.keys(SIG_NUMBER_WORDS)
  .map((w) => w.replaceAll(/[ -]/g, '[ -]'))
  .join('|');
const SIG_NUMBER_WORDS_RE = new RegExp(String.raw`\b(?:${SIG_NUMBER_WORDS_ALTERNATION})\b`, 'gi');

const SIG_DOSE_VERB_RE = /\b(?:take|apply|use|inject|inhale|instill|insert|chew|dissolve|spray)\b/;
/** Captures `<low>(?:[-/to] <high>)?` after a verb. Matched against the substring following the verb. */
const SIG_DOSE_RANGE_RE = /^\s+(\d+(?:\.\d+)?)(?:\s*(?:-|to)\s*(\d+(?:\.\d+)?))?/;

// The keyword rules run on text `digitizeSigNumberWords` has already rewritten, so "three times daily"
// arrives as "3 times daily" and each rule matches the digit form as well as the word.
const SIG_QID_RE = /\bqid\b|four\s+times|\b4\s*(?:x|times)\b/;
const SIG_TID_RE = /\btid\b|three\s+times|\b3\s*(?:x|times)\b/;
const SIG_BID_RE = /\bbid\b|\btwice\b|two\s+times|\b2\s*(?:x|times)\b/;
const SIG_DAILY_RE = /once\s+(?:a\s+)?day|once\s+daily|every\s+day|\bqd\b|\bqday\b|\bdaily\b/;
const SIG_EVERY_HOURS_RE = /every\s+(\d+(?:\.\d+)?)\s*(?:hr|hour|hours)\b|\bq(\d+)h\b/;
const SIG_EVERY_DAYS_RE = /every\s+(\d+(?:\.\d+)?)\s*(?:d|day|days)\b/;

/** As-needed (PRN) markers. A PRN med has no fixed daily rate, so days supply can't be derived — even
 * when the sig also names a time-of-day anchor ("Take 1 tablet at bedtime as needed for insomnia").
 * Checked before any frequency inference so a PRN sig always falls through to `undefined`. */
const SIG_PRN_RE = /\bas needed\b|\bp\.?r\.?n\.?\b/;

/** Whether a sig line marks the medication as-needed (PRN) rather than on a fixed schedule. */
export function isAsNeededSig(sigLine: string): boolean {
  return SIG_PRN_RE.test(sigLine.toLowerCase());
}

/**
 * Time-of-day / meal-anchored frequency phrasing ScriptSure uses in place of an explicit
 * "daily"/"bid" keyword (e.g. "with the evening meal", "at bedtime", "morning and evening"). Ordered
 * two-per-day before one-per-day so "morning and evening" isn't caught by the "morning" rule; three
 * meals ("with meals") is the standard pharmacy reading of an unqualified meal instruction.
 *
 * These are deliberately conservative — only unambiguous anchors are mapped. Anything else falls
 * through to `undefined` so the caller keeps its default rather than guessing a rate.
 */
const SIG_TID_MEALS_RE = /with (?:each|every) meal|with meals\b|breakfast,? lunch,? and dinner/;
const SIG_BID_MEALS_RE =
  /morning and (?:evening|night|bedtime)|breakfast and (?:dinner|supper|evening meal)|with breakfast and dinner/;
const SIG_DAILY_ANCHOR_RE =
  /\bqhs\b|\bqam\b|\bqpm\b|at bedtime|\bnightly\b|at night\b|(?:in the|every) (?:morning|evening|afternoon|night)\b|with (?:the )?(?:morning|evening|breakfast|dinner|bedtime) meal|with breakfast\b|with dinner\b|with (?:the )?(?:morning|evening) dose/;

/** Replace spelled-out English numbers with digits so the interval regexes can capture them.
 * Idempotent on text that already uses digits; case-insensitive. */
function digitizeSigNumberWords(text: string): string {
  return text.replace(SIG_NUMBER_WORDS_RE, (match) => {
    const key = match.toLowerCase();
    const direct = SIG_NUMBER_WORDS[key];
    if (direct !== undefined) {
      return String(direct);
    }
    const collapsed = key.replaceAll(/[ -]/g, '');
    const collapsedHit = SIG_NUMBER_WORDS[collapsed];
    return collapsedHit === undefined ? match : String(collapsedHit);
  });
}

/** Per-dose amount from a sig ("take 2 tablets" → 2; "apply 1-2 patches" → 2, the high end).
 * Defaults to 1 when no verb / number is found. `text` must be lowercased. */
function inferPerDoseFromSigText(text: string): number {
  const verbMatch = SIG_DOSE_VERB_RE.exec(text);
  if (verbMatch?.index === undefined) {
    return 1;
  }
  const after = text.slice(verbMatch.index + verbMatch[0].length);
  const range = SIG_DOSE_RANGE_RE.exec(after);
  if (!range) {
    return 1;
  }
  const lo = Number.parseFloat(range[1]!);
  const hi = range[2] ? Number.parseFloat(range[2]) : lo;
  return Number.isFinite(hi) && hi > 0 ? hi : lo;
}

/**
 * Per-dose amount from a free-text sig line ("Take 2 tablets by mouth" → 2; "apply 1-2 patches" → 2,
 * the high end), independent of whether a frequency is present. Defaults to 1 when no dose verb/number
 * is found. Unlike {@link inferDailyDoseFromSig} this does not require a recognizable frequency — so it
 * still seeds the dose for sigs like "Take 1 tablet by mouth with the evening meal".
 */
export function inferPerDoseFromSig(sigLine: string): number {
  if (!sigLine) {
    return 1;
  }
  return inferPerDoseFromSigText(digitizeSigNumberWords(sigLine.toLowerCase()));
}

/** Doses-per-day from frequency keywords (qid/tid/bid/daily) or a time-of-day/meal anchor
 * ("with the evening meal", "at bedtime", "morning and evening"), or 0 when none recognized. */
function inferDosesPerDayKeyword(text: string): number {
  if (SIG_QID_RE.test(text)) {
    return 4;
  }
  if (SIG_TID_RE.test(text) || SIG_TID_MEALS_RE.test(text)) {
    return 3;
  }
  if (SIG_BID_RE.test(text) || SIG_BID_MEALS_RE.test(text)) {
    return 2;
  }
  if (SIG_DAILY_RE.test(text) || SIG_DAILY_ANCHOR_RE.test(text)) {
    return 1;
  }
  return 0;
}

/** Doses-per-day from intervals ("every 8 hours", "q6h", "every 2 days"), or 0 when none recognized. */
function inferDosesPerDayFromInterval(text: string): number {
  const everyHrs = SIG_EVERY_HOURS_RE.exec(text);
  if (everyHrs) {
    const h = Number.parseFloat(everyHrs[1] ?? everyHrs[2]!);
    if (Number.isFinite(h) && h > 0) {
      return 24 / h;
    }
  }
  const everyDays = SIG_EVERY_DAYS_RE.exec(text);
  if (everyDays) {
    const d = Number.parseFloat(everyDays[1]!);
    if (Number.isFinite(d) && d > 0) {
      return 1 / d;
    }
  }
  return 0;
}

/**
 * Doses per day the sig states, or 0 when it states none. Unlike {@link inferDailyDoseFromSig} this
 * reads a PRN sig's cadence too: "every 6 hours as needed" names a maximum of 4 doses a day, which
 * is a real instruction even though no fixed daily rate follows from it.
 */
export function inferDosesPerDayFromSig(sigLine: string): number {
  const text = digitizeSigNumberWords(sigLine.toLowerCase());
  return inferDosesPerDayKeyword(text) || inferDosesPerDayFromInterval(text);
}

/**
 * Parse a free-text sig line into a per-dose × per-day estimate. Returns `undefined` when no
 * frequency keyword is recognized, so the caller can leave a prior days-supply value untouched
 * rather than guessing.
 */
export function inferDailyDoseFromSig(sigLine: string): { perDose: number; dosesPerDay: number } | undefined {
  if (!sigLine) {
    return undefined;
  }
  const text = digitizeSigNumberWords(sigLine.toLowerCase());
  // A PRN med is taken on demand, so there is no fixed daily rate to bill days supply against, even
  // when the sig also carries a time-of-day/meal anchor. Bail before inferring a rate. The cadence
  // itself is still readable — `inferDosesPerDayFromSig` reads it as the ceiling it states.
  if (SIG_PRN_RE.test(text)) {
    return undefined;
  }
  const dosesPerDay = inferDosesPerDayFromSig(sigLine);
  if (!dosesPerDay) {
    return undefined;
  }
  return { perDose: inferPerDoseFromSigText(text), dosesPerDay };
}

/**
 * Estimate days supply from a sig line and dispense quantity. Returns `undefined` when the quantity
 * is non-positive or the sig has no parseable frequency.
 */
export function inferDaysSupplyFromSig(sigLine: string, quantity: number): number | undefined {
  if (!quantity || quantity <= 0) {
    return undefined;
  }
  const inferred = inferDailyDoseFromSig(sigLine);
  if (!inferred) {
    return undefined;
  }
  const perDay = inferred.perDose * inferred.dosesPerDay;
  if (perDay <= 0) {
    return undefined;
  }
  const days = Math.floor(quantity / perDay);
  return days > 0 ? days : undefined;
}

/** A structured sig read off a ScriptSure `$drug-search` formulation result. */
export interface ParsedSig {
  sigLine: string;
  /** The template's saved dispense quantity, or `undefined` when the template names none — kept
   * distinct so a caller prefilling dispense detail doesn't mistake "unspecified" for "dispense 1". */
  quantity: number | undefined;
  /** Resolved NCI potency-unit code (ScriptSure's value when explicit, else inferred, else tablet). */
  quantityQualifier: string;
  /** Raw value ScriptSure sent (undefined when omitted) — lets callers tell "explicit" from "inferred". */
  quantityQualifierRaw: string | undefined;
}

/**
 * Read the structured sigs ScriptSure stamps on a `$drug-search` formulation `Medication` (under the
 * {@link SCRIPTSURE_SIG_EXTENSION} extension), resolving each sig's dispense unit via `matcher`.
 */
export function parseScriptSureSigs(medication: Medication, matcher: QualifierMatcher): ParsedSig[] {
  const formatText = medication.code?.text;
  const out: ParsedSig[] = [];
  for (const ext of medication.extension ?? []) {
    if (ext.url !== SCRIPTSURE_SIG_EXTENSION) {
      continue;
    }
    const nested = ext.extension ?? [];
    const sigLine = nested.find((n) => n.url === 'sigLine')?.valueString ?? '';
    const quantity = nested.find((n) => n.url === 'quantity')?.valueInteger;
    const quantityQualifierRaw = nested.find((n) => n.url === 'quantityQualifier')?.valueString;
    if (sigLine) {
      out.push({
        sigLine,
        quantity,
        quantityQualifier: resolveQuantityQualifier(quantityQualifierRaw, sigLine, formatText, matcher),
        quantityQualifierRaw,
      });
    }
  }
  return out;
}
